import { useEffect } from 'react';
import { useCqgDisplayState } from '@features/cqg/hooks/useCqgDisplayState';
import { readStoredCqgCreds } from '@features/cqg/lib/cqgCreds';
import { useRithmicDisplayState } from '@features/rithmic/hooks/useRithmicDisplayState';
import { readStoredRithmicCreds } from '@features/rithmic/lib/rithmicCreds';
import { rememberAccountLogins } from '../lib/accountLogins';

/**
 * Records, per account, the USERNAME (+ CQG environment / Rithmic system) that connected it — into
 * the FE-only per-account login memory (`accountLogins.ts`, localStorage, never the DB; user
 * directive 2026-07-18/2026-07-21). Fires whenever a live provider session's owned-accounts list is
 * known, using the currently-stored creds (the ones that just logged in). Covers every connect path
 * (dialog, Add-account, onboarding, auto-resume) because they all funnel through a live session.
 *
 * Mount once near the app root (AppShell). Both CQG and Rithmic are recorded so the Accounts panel
 * can prefill either provider's connect dialog for the specific row the user clicks Connect on.
 */
export function useAccountLoginRecorder(): void {
  // ── CQG: username + demo/live environment ─────────────────────────────────
  const cqg = useCqgDisplayState();
  // Join to a stable string so the effect only re-runs when the OWNED SET changes, not on every
  // status poll's fresh array identity. The effect reconstructs the names from `key`, so `[display,
  // key]` is genuinely exhaustive (no reference to the unstable `accounts` array inside).
  const cqgKey = cqg.accounts.join(',');
  useEffect(() => {
    if (cqg.display !== 'live' || cqgKey === '') return;
    const creds = readStoredCqgCreds();
    if (!creds.username) return;
    rememberAccountLogins(cqgKey.split(','), { username: creds.username, environment: creds.environment });
  }, [cqg.display, cqgKey]);

  // ── Rithmic: username + system (no demo/live environment) ─────────────────
  const rithmic = useRithmicDisplayState();
  const rithmicKey = rithmic.accounts.join(',');
  useEffect(() => {
    if (rithmic.display !== 'live' || rithmicKey === '') return;
    const creds = readStoredRithmicCreds();
    if (!creds.username) return;
    rememberAccountLogins(rithmicKey.split(','), { username: creds.username, environment: null, system: creds.system });
  }, [rithmic.display, rithmicKey]);
}
